import {
  clearPendingTurn, getPendingChunk, getPendingChunks, getPendingTurn, savePendingChunk, savePendingTurn,
  removePendingChunk, voiceTurnKey, archivePendingTurn,
} from './voiceAudioQueue';
import { withVoiceDeadline } from './voiceTurnDecision';

const CHUNK_BYTES = 10 * 16000 * 2;
const DRAFT_BYTES = 16000 * 2;

const failureError = (failure) => Object.assign(new Error(failure.code || 'unclear_audio'), {
  status: failure.status, code: failure.code, reason: failure.reason,
  audioRetained: failure.audio_retained, occurredAt: failure.occurred_at, recoveryAction: failure.recovery_action,
});

const deliveryPending = () => Object.assign(new Error('Your answer is still being processed. Checking delivery.'), {
  code: 'delivery_unconfirmed',
});

function concat(chunks) {
  const size = chunks.reduce((sum, chunk) => sum + chunk.byteLength, 0);
  const merged = new Uint8Array(size);
  let offset = 0;
  chunks.forEach((chunk) => { merged.set(new Uint8Array(chunk), offset); offset += chunk.byteLength; });
  return merged.buffer;
}

export class VoiceUploadManager {
  constructor(sessionId, candidateId, api, saved = null) {
    this.key = voiceTurnKey(sessionId, candidateId);
    this.api = api;
    this.turn = saved || {
      key: this.key, utteranceId: crypto.randomUUID(), nextSeq: 0,
      clientTurnId: null, caption: '', finalCaption: '', finalizing: false,
      captionSource: 'unknown', diagnostics: {},
    };
    this.frames = [];
    this.frameBytes = 0;
    this.draftedBytes = 0;
    this.queue = Promise.resolve();
    this.captionQueue = Promise.resolve();
    this.closed = false;
    this.lastError = null;
    this.inFlight = null;
  }

  static async restore(sessionId, candidateId, api) {
    const saved = await getPendingTurn(voiceTurnKey(sessionId, candidateId));
    return saved ? new VoiceUploadManager(sessionId, candidateId, api, saved) : null;
  }

  async start() {
    await this.enqueue(() => savePendingTurn(this.turn));
    if (this.lastError) throw this.lastError;
  }

  async discard() {
    this.closed = true;
    await this.queue;
    await this.captionQueue;
    await clearPendingTurn(this.key);
  }

  async rememberFailure(error) {
    if (![422, 503].includes(error.status) || !['unclear_audio', 'no_speech', 'speech_service_unavailable'].includes(error.code)) return;
    this.turn.lastFailure = { status: error.status, code: error.code, reason: error.reason,
      audio_retained: error.audioRetained, recovery_action: error.recoveryAction,
      occurred_at: error.occurredAt || new Date().toISOString() };
    this.turn.deliveryState = 'rejected';
    await savePendingTurn(this.turn);
  }

  async archiveForRepeat(recovery) {
    await this.queue;
    await this.captionQueue;
    this.turn.recovery = recovery;
    await archivePendingTurn(this.turn);
    this.turn.deliveryState = 'needs_repeat';
    this.closed = true;
  }

  async inspectDelivery() {
    const status = await withVoiceDeadline(this.api.uploadStatus(this.turn.utteranceId), 10000);
    if (status.committed && status.response) return status;
    if (!status.processing && !this.inFlight) {
      const failure = status.last_failure || this.turn.lastFailure;
      if (failure) {
        await this.rememberFailure(failureError(failure));
        return { ...status, last_failure: failure };
      }
    }
    return { ...status, processing: status.processing || Boolean(this.inFlight) };
  }

  async resume() {
    const status = await this.inspectDelivery();
    if (status.committed && status.response) return this.accept(status.response);
    if (status.processing) throw deliveryPending();
    if (status.last_failure) throw failureError(status.last_failure);
    if (this.turn.deliveryState === 'needs_repeat') throw failureError(this.turn.lastFailure || {
      status: 422, code: 'unclear_audio', reason: 'interrupted_recovery', audio_retained: true,
    });
    return this.reconcile();
  }

  async accept(response) {
    this.closed = true;
    await this.captionQueue;
    await clearPendingTurn(this.key);
    return response;
  }

  enqueue(task) {
    this.queue = this.queue.then(task).catch((error) => { this.lastError = error; });
    return this.queue;
  }

  addFrame(frame) {
    this.frames.push(frame);
    this.frameBytes += frame.byteLength;
    if (this.frameBytes >= CHUNK_BYTES) this.flush();
    else if (this.frameBytes - this.draftedBytes >= DRAFT_BYTES) {
      const seq = this.turn.nextSeq;
      const draft = concat(this.frames);
      this.draftedBytes = this.frameBytes;
      this.enqueue(() => savePendingChunk(this.key, seq, draft));
    }
  }

  flush() {
    if (!this.frameBytes) return;
    const seq = this.turn.nextSeq++;
    const pcm = concat(this.frames);
    this.frames = [];
    this.frameBytes = 0;
    this.draftedBytes = 0;
    this.enqueue(async () => {
      await savePendingChunk(this.key, seq, pcm);
      await savePendingTurn(this.turn);
      await this.api.uploadChunk(this.turn.utteranceId, seq, pcm);
      await removePendingChunk(this.key, seq);
    });
  }

  async saveCaption(running, finalText, source, recognition = null) {
    if (this.closed) return;
    this.turn.caption = running || this.turn.caption || '';
    this.turn.finalCaption = finalText || this.turn.finalCaption || '';
    this.turn.captionSource = source || this.turn.captionSource;
    if (recognition) this.turn.recognitionDiagnostics = recognition;
    // Local captions must not wait for a slow or offline chunk upload.
    const saved = this.captionQueue.then(() => savePendingTurn(this.turn));
    this.captionQueue = saved.catch((error) => { this.lastError = error; });
    await saved;
  }

  async finalize(clientTurnId, diagnostics) {
    this.turn.clientTurnId = clientTurnId;
    this.turn.diagnostics = diagnostics;
    this.turn.finalizing = true;
    this.flush();
    return this.reconcile();
  }

  async finalizeInterrupted(clientTurnId) {
    const drafts = await getPendingChunks(this.key);
    const lastDraft = drafts.length ? drafts[drafts.length - 1].seq + 1 : 0;
    this.turn.nextSeq = Math.max(this.turn.nextSeq, lastDraft);
    this.turn.clientTurnId = clientTurnId;
    this.turn.finalizing = true;
    await savePendingTurn(this.turn);
    return this.reconcile();
  }

  async reconcile() {
    if (this.inFlight) throw deliveryPending();
    const work = this.reconcileSaved().catch(async (error) => {
      await this.rememberFailure(error);
      throw error;
    });
    this.inFlight = work;
    work.finally(() => { if (this.inFlight === work) this.inFlight = null; }).catch(() => {});
    try {
      return await withVoiceDeadline(work);
    } catch (error) {
      if (error.code === 'delivery_unconfirmed') {
        this.turn.deliveryState = 'unconfirmed';
        await savePendingTurn(this.turn);
      }
      throw error;
    }
  }

  async reconcileSaved() {
    await this.queue;
    await this.captionQueue;
    await savePendingTurn(this.turn);
    const { utteranceId, nextSeq, clientTurnId } = this.turn;
    const status = await this.api.uploadStatus(utteranceId);
    if (status.committed && status.response) {
      return this.accept(status.response);
    }
    if (status.processing) throw deliveryPending();
    const received = new Set(status.received_sequences || []);
    for (let seq = 0; seq < nextSeq; seq += 1) {
      if (received.has(seq)) continue;
      const pending = await getPendingChunk(this.key, seq);
      if (!pending) throw new Error(`Audio chunk ${seq} is missing; keep this answer pending`);
      await this.api.uploadChunk(utteranceId, seq, pending.pcm);
      await removePendingChunk(this.key, seq);
    }
    const response = await this.api.finalizeUpload(utteranceId, {
      chunk_count: nextSeq, client_turn_id: clientTurnId,
      browser_caption: this.turn.finalCaption,
      browser_interim_caption: this.turn.caption,
      caption_final: Boolean(this.turn.finalCaption),
      caption_source: this.turn.captionSource,
      capture_metrics: this.turn.diagnostics,
      recognition_diagnostics: this.turn.recognitionDiagnostics || {},
    });
    return this.accept(response);
  }
}
